/**
 * An agent's copilot (V1-E09-T04, design 14 §4): ask it for a change, review the agent's failing calls (their
 * clusters, each with a fix to propose), and the proposals: what they change and why, the diff, and how the
 * agent's eval suites went on the proposal beside the current version. The copilot only proposes: a person
 * accepts (a new draft version) and publishes.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { ApiError, data, type Schemas } from "../../api/client";
import { Section, useApiQuery } from "../../app/Page";
import { useBff, useCan } from "../../app/session";
import { useFormat, useT } from "../../i18n";
import { Button } from "../../ui/Button";
import { ErrorState } from "../../ui/states";
import { SpecDiff } from "../versions/SpecDiff";

type Agent = Schemas["AgentOut"];
type Proposal = Schemas["ProposalOut"];
type Review = Schemas["ReviewOut"];
type Brief = Schemas["RunBrief"];

const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm";

/** Every query the copilot's answers change: its own lists, and the agent's (an accepted proposal is a version). */
function useRefresh(agentId: string) {
  const queries = useQueryClient();
  return () => queries.invalidateQueries({
    predicate: (q) => String(q.queryKey[0]).startsWith(`agents:${agentId}`) || String(q.queryKey[0]).startsWith(`evals:${agentId}`),
  });
}

/** A copilot error worth its own words: no model set yet. */
export function CopilotError({ error }: { error: unknown }) {
  const t = useT();
  if (error instanceof ApiError && error.status === 409 && /copilot model/.test(error.problem?.detail ?? "")) {
    return (
      <p role="alert" className="text-sm text-[var(--color-danger)]">
        {t("copilot.noModel")}{" "}
        <Link to="/settings" className="text-[var(--color-primary)] underline">{t("copilot.noModelLink")}</Link>
      </p>
    );
  }
  return <ErrorState error={error} />;
}

export function Copilot({ agent }: { agent: Agent }) {
  const t = useT();
  const canWrite = useCan("agents:write");
  const proposals = useApiQuery(`agents:${agent.id}:copilot:proposals`, (a) =>
    a.GET("/v1/agents/{agent_id}/copilot/proposals", { params: { path: { agent_id: agent.id } } }),
  { refetchInterval: (page) => (page?.data.some((p) => p.evaluation.state === "running") ? 2000 : false) });
  const reviews = useApiQuery(`agents:${agent.id}:copilot:reviews`, (a) =>
    a.GET("/v1/agents/{agent_id}/copilot/reviews", { params: { path: { agent_id: agent.id } } }),
  { refetchInterval: (page) => (page?.data.some((r) => r.status === "queued" || r.status === "running") ? 2000 : false) });
  return (
    <div className="space-y-6">
      <p className="text-sm text-[var(--color-muted)]">{t("copilot.intro")}</p>
      {canWrite && <Ask agent={agent} />}
      <Section title={t("copilot.proposals")} query={proposals}>
        {(page) => page.data.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">{t("copilot.noProposals")}</p>
        ) : (
          <ul className="space-y-4">{page.data.map((p) => <ProposalCard key={p.id} agent={agent} proposal={p} canWrite={canWrite} />)}</ul>
        )}
      </Section>
      <Section title={t("copilot.reviews")} query={reviews}>
        {(page) => <Reviews agent={agent} reviews={page.data} canWrite={canWrite} />}
      </Section>
    </div>
  );
}

/** "What should change?": a request in words, proposed as changes. */
function Ask({ agent }: { agent: Agent }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const refresh = useRefresh(agent.id);
  const [request, setRequest] = useState("");
  const propose = useMutation({
    mutationFn: () => data(api.POST("/v1/agents/{agent_id}/copilot/proposals", {
      params: { path: { agent_id: agent.id } }, body: { request: request.trim() },
    })),
    onSuccess: async () => { setRequest(""); await refresh(); },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (request.trim().length >= 3) propose.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border border-[var(--color-border)] p-4">
      <label htmlFor={id} className="block text-sm font-medium">{t("copilot.ask")}</label>
      <textarea id={id} rows={3} value={request} onChange={(e) => setRequest(e.target.value)}
        placeholder={t("copilot.askPlaceholder")} className={field} aria-describedby={`${id}-hint`} />
      <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">{t("copilot.askHint")}</p>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={request.trim().length < 3 || propose.isPending}>
          {propose.isPending ? <Loader2 size={16} aria-hidden="true" className="animate-spin" /> : <Sparkles size={16} aria-hidden="true" />}
          {t(propose.isPending ? "copilot.proposing" : "copilot.propose")}
        </Button>
      </div>
      {propose.isError && <CopilotError error={propose.error} />}
    </form>
  );
}

function brief(t: ReturnType<typeof useT>, run: Brief): string {
  if (run.status === "queued" || run.status === "running") return t("copilot.eval.running");
  if (run.status === "error") return t("copilot.eval.error");
  return t("copilot.eval.passed", { passed: run.passed ?? 0, total: run.total ?? 0 });
}

function ProposalCard({ agent, proposal: p, canWrite }: { agent: Agent; proposal: Proposal; canWrite: boolean }) {
  const t = useT();
  const format = useFormat();
  const { api } = useBff();
  const refresh = useRefresh(agent.id);
  const base = useApiQuery(`agents:${agent.id}:v${p.base_version}`, (a) =>
    a.GET("/v1/agents/{agent_id}/versions/{version}", { params: { path: { agent_id: agent.id, version: p.base_version } } }));
  const decide = useMutation({
    mutationFn: (what: "accept" | "dismiss") => data(what === "accept"
      ? api.POST("/v1/copilot/proposals/{proposal_id}:accept", { params: { path: { proposal_id: p.id } } })
      : api.POST("/v1/copilot/proposals/{proposal_id}:dismiss", { params: { path: { proposal_id: p.id } } })),
    onSuccess: refresh,
  });
  const stale = decide.error instanceof ApiError && decide.error.status === 412;
  const source = p.source as { kind?: string; title?: string; call_id?: string };
  return (
    <li className="space-y-3 rounded-lg border border-[var(--color-border)] p-4" aria-labelledby={`${p.id}-title`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id={`${p.id}-title`} className="font-semibold">{p.summary || t("copilot.untitled")}</h3>
          <p className="text-xs text-[var(--color-muted)]">
            {t(`copilot.source.${source.kind === "cluster" ? "cluster" : source.kind === "call" ? "call" : "request"}`,
              { title: source.title ?? "", call: source.call_id ?? "" })}
            {" · "}{t("copilot.onVersion", { version: p.base_version })}{" · "}{format.dateTime(p.created_at)}
          </p>
        </div>
        <span className="rounded bg-[var(--color-surface)] px-2 py-0.5 text-xs font-medium">
          {p.status === "accepted" ? t("copilot.accepted", { version: p.accepted_version ?? 0 }) : t(`copilot.status.${p.status}`)}
        </span>
      </div>
      <ul className="list-inside list-disc space-y-1 text-sm">
        {p.changes.map((c, i) => (
          <li key={i}><code className="text-xs">{String(c.op)} {String(c.path)}</code> {String(c.why ?? "")}</li>
        ))}
      </ul>
      {base.data && <SpecDiff before={base.data.spec} after={p.document} label={t("copilot.diffLabel", { summary: p.summary })} />}
      <Evaluation proposal={p} />
      {p.status === "proposed" && canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => decide.mutate("accept")} disabled={decide.isPending}>
            <Check size={16} aria-hidden="true" />{t("copilot.accept")}
          </Button>
          <Button variant="secondary" onClick={() => decide.mutate("dismiss")} disabled={decide.isPending}>
            <X size={16} aria-hidden="true" />{t("copilot.dismiss")}
          </Button>
        </div>
      )}
      {p.status === "accepted" && (
        <p role="status" className="text-sm">
          {t("copilot.acceptedNote", { version: p.accepted_version ?? 0 })}{" "}
          <Link to="/agents/$agentId" params={{ agentId: agent.id }} search={{ tab: "build" }}
            className="text-[var(--color-primary)] underline">{t("copilot.openBuilder")}</Link>
        </p>
      )}
      {stale && <p role="alert" className="text-sm text-[var(--color-danger)]">{t("copilot.stale", { version: p.base_version })}</p>}
      {decide.isError && !stale && <ErrorState error={decide.error} />}
    </li>
  );
}

function Evaluation({ proposal: p }: { proposal: Proposal }) {
  const t = useT();
  const e = p.evaluation;
  if (e.state === "none") return <p className="text-sm text-[var(--color-muted)]">{e.note ?? t("copilot.eval.none")}</p>;
  return (
    <table className="w-full text-sm">
      <caption className="text-left text-sm font-medium">
        {e.state === "running" ? t("copilot.eval.evaluating") : t("copilot.eval.title")}
      </caption>
      <thead>
        <tr className="text-left text-xs text-[var(--color-muted)]">
          <th scope="col" className="py-1">{t("copilot.eval.suite")}</th>
          <th scope="col">{t("copilot.eval.before", { version: p.base_version })}</th>
          <th scope="col">{t("copilot.eval.after")}</th>
        </tr>
      </thead>
      <tbody>
        {e.suites.map((s) => (
          <tr key={s.suite_id} className="border-t border-[var(--color-border)]">
            <th scope="row" className="py-1 text-left font-normal">{s.suite}</th>
            <td>{brief(t, s.base)}</td>
            <td>{brief(t, s.proposal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Reviews({ agent, reviews, canWrite }: { agent: Agent; reviews: Review[]; canWrite: boolean }) {
  const t = useT();
  const { api } = useBff();
  const refresh = useRefresh(agent.id);
  const [days, setDays] = useState("7");
  const id = useId();
  const start = useMutation({
    mutationFn: () => {
      const until = new Date();
      const since = new Date(until.getTime() - Number(days) * 86_400_000);
      return data(api.POST("/v1/agents/{agent_id}/copilot/reviews", {
        params: { path: { agent_id: agent.id } }, body: { since: since.toISOString(), until: until.toISOString() },
      }));
    },
    onSuccess: refresh,
  });
  const latest = reviews[0];
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-muted)]">{t("copilot.reviewIntro")}</p>
      {canWrite && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label htmlFor={id} className="block text-sm font-medium">{t("copilot.period")}</label>
            <select id={id} value={days} onChange={(e) => setDays(e.target.value)} className={`${field} w-auto`}>
              {["1", "7", "30", "90"].map((d) => <option key={d} value={d}>{t("copilot.lastDays", { days: Number(d) })}</option>)}
            </select>
          </div>
          <Button variant="secondary" onClick={() => start.mutate()} disabled={start.isPending}>{t("copilot.review")}</Button>
        </div>
      )}
      {start.isError && <CopilotError error={start.error} />}
      {latest ? <ReviewView agent={agent} review={latest} canWrite={canWrite} /> : (
        <p className="text-sm text-[var(--color-muted)]">{t("copilot.noReviews")}</p>
      )}
    </div>
  );
}

function ReviewView({ agent, review: r, canWrite }: { agent: Agent; review: Review; canWrite: boolean }) {
  const t = useT();
  const format = useFormat();
  if (r.status === "queued" || r.status === "running")
    return <p role="status" className="flex items-center gap-2 text-sm"><Loader2 size={14} aria-hidden="true" className="animate-spin" />{t("copilot.reviewing")}</p>;
  if (r.status === "failed") return <p role="alert" className="text-sm text-[var(--color-danger)]">{t("copilot.reviewFailed", { error: r.error ?? "" })}</p>;
  return (
    <div className="space-y-3">
      <p role="status" className="text-sm">
        {t("copilot.reviewDone", { failing: r.failing, seen: r.calls_seen, from: format.date(r.since), to: format.date(r.until) })}
        {r.method && <span className="text-[var(--color-muted)]"> · {t(`copilot.method.${r.method}`)}</span>}
      </p>
      {r.clusters.length === 0 ? <p className="text-sm text-[var(--color-muted)]">{t("copilot.noFailures")}</p> : (
        <ol className="space-y-3">
          {r.clusters.map((c, i) => <ClusterCard key={i} agent={agent} review={r} index={i} cluster={c} canWrite={canWrite} />)}
        </ol>
      )}
    </div>
  );
}

function ClusterCard({ agent, review, index, cluster: c, canWrite }: {
  agent: Agent; review: Review; index: number; cluster: Schemas["ClusterOut"]; canWrite: boolean;
}) {
  const t = useT();
  const { api } = useBff();
  const refresh = useRefresh(agent.id);
  const propose = useMutation({
    mutationFn: () => data(api.POST("/v1/agents/{agent_id}/copilot/proposals", {
      params: { path: { agent_id: agent.id } }, body: { review: { review_id: review.id, cluster: index } },
    })),
    onSuccess: refresh,
  });
  const title = c.title ?? t("copilot.unnamed");
  return (
    <li className="space-y-2 rounded-lg border border-[var(--color-border)] p-4" aria-label={t("copilot.clusterLabel", { title, size: c.size })}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-semibold">{title}</h4>
        <span className="text-xs text-[var(--color-muted)]">{t("copilot.calls", { count: c.size })}</span>
      </div>
      {c.failure_mode && <p className="text-sm">{c.failure_mode}</p>}
      {c.likely_cause && <p className="text-sm"><span className="font-medium">{t("copilot.likelyCause")}</span> {c.likely_cause}</p>}
      <details className="text-sm">
        <summary className="cursor-pointer text-[var(--color-primary)]">{t("copilot.examples", { count: c.examples.length })}</summary>
        <ul className="mt-2 space-y-2">
          {c.examples.map((e) => (
            <li key={e.call_id} className="rounded-md bg-[var(--color-surface)] p-2">
              <Link to="/calls/$callId" params={{ callId: e.call_id }} className="font-mono text-xs text-[var(--color-primary)] underline">{e.call_id}</Link>
              <span className="ml-2 text-xs text-[var(--color-muted)]">{e.reasons.join("; ")}</span>
              <pre className="mt-1 whitespace-pre-wrap text-xs">{e.evidence}</pre>
            </li>
          ))}
        </ul>
      </details>
      {canWrite && (
        <Button variant="secondary" onClick={() => propose.mutate()} disabled={propose.isPending}>
          <Sparkles size={16} aria-hidden="true" />{t(propose.isPending ? "copilot.proposing" : "copilot.proposeFix")}
        </Button>
      )}
      {propose.isSuccess && <p role="status" className="text-sm">{t("copilot.proposed")}</p>}
      {propose.isError && <CopilotError error={propose.error} />}
    </li>
  );
}
